import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Action, Dispatch } from 'redux';

import { PredefinedMilisecondsTypeEnum } from '../../lib/enums';
import lang from '../../lib/language';
import { RootState } from '../../lib/store';
import { addOrRemoveSymbolToFavoriteList } from '../../lib/store/common-actions';
import { getCompanyLogoAndName as getLogoAndName } from '../../lib/store/fundamental';
import { isSymbolInFavourites } from '../../lib/store/helpers';
import { ChartDataRange } from '../../lib/store/market-data/charting/types';
import { SymbolFavoriteListActionType, SymbolFavoriteListPayload } from '../../lib/store/market-data/types';
import { getCompany, getFavoriteStocks } from '../../lib/store/selectors';
import SymbolStatusCache from '../../lib/store-util/SymbolStatusCache';
import { FAVOURITE_SYMBOLS_MAX_LIMIT } from '../../lib/util/constants';
import { checkIfSymbolIsHalted } from '../../lib/util/HaltSymbolHelper';

import InfoIcon from '../../assets/img/info-circle-red.svg';
import { useIsMobile } from '../../hooks';
import ChartHeader from '../Chart/SymbolChart/ChartHeader/ChartHeader';
import FavouriteSymbolsLimitModal from '../FavouriteSymbolsLimitModal/FavouriteSymbolsLimitModal';
import FavouritesModal from '../StocksResult/componets/FavouritesModal';

import styles from './SymbolDetailsHeader.module.scss';

type OwnProps = {
  symbol: string;
  currentRange: ChartDataRange;
  handleCreateOrderSellOrBuy: (target: string, isHeaderBtn: boolean) => void;
  isUserRestricted?: boolean;
};
type Props = OwnProps & ReduxProps;

const SymbolDetailsHeader = (props: Props) => {
  const {
    symbol,
    currentRange,
    companyLogoAndName,
    favouriteStocks,
    individual,
    companyInfo,
    isUserRestricted,
    getCompanyLogoAndName,
    addOrRemoveFromFavoriteList,
    handleCreateOrderSellOrBuy,
  } = props;
  const { isMobile } = useIsMobile();

  const [showFavouritesLimitMessage, setShowFavouritesLimitMessage] = useState<boolean>(false);
  const [isAddedToFavourites, setIsAddedToFavourites] = useState<boolean>(false);
  const [isRemovedFromFavourites, setIsRemovedFromFavourites] = useState<boolean>(false);
  const symbolsStatusCache = SymbolStatusCache.use(symbol);

  const toggleFavoritesSubscription = useCallback(() => {
    const userCorrelationId = individual?.user_correlation_id;
    if (!userCorrelationId) {
      return;
    }

    if (isSymbolInFavourites(favouriteStocks, symbol)) {
      addOrRemoveFromFavoriteList({
        symbolAndExchange: {
          symbol,
          stockExchangeCode: companyInfo?.stockExchangeCode,
          countryDesignation: companyInfo?.countryDesignation,
        },
        actionType: SymbolFavoriteListActionType.Remove,
        userCorrelationId,
      });
      setIsRemovedFromFavourites(true);
    } else if (favouriteStocks.length >= FAVOURITE_SYMBOLS_MAX_LIMIT) {
      setShowFavouritesLimitMessage(true);
    } else {
      addOrRemoveFromFavoriteList({
        symbolAndExchange: {
          symbol,
          stockExchangeCode: companyInfo?.stockExchangeCode,
          countryDesignation: companyInfo?.countryDesignation,
        },
        actionType: SymbolFavoriteListActionType.Add,
        userCorrelationId,
      });
      setIsAddedToFavourites(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    favouriteStocks,
    addOrRemoveFromFavoriteList,
    companyInfo,
    individual?.user_correlation_id,
    symbol,
  ]);

  const logo = useMemo(() => (companyLogoAndName[symbol]
    ? companyLogoAndName[symbol]?.logo : undefined), [companyLogoAndName, symbol]);

  const url = useMemo(() => (companyLogoAndName[symbol]
    ? companyLogoAndName[symbol]?.url : undefined), [companyLogoAndName, symbol]);

  const headerProps = {
    symbol,
    logo,
    url,
    company: companyLogoAndName[symbol]?.name || companyInfo?.companyName,
  };

  useEffect(() => {
    if (!companyLogoAndName[symbol]) {
      getCompanyLogoAndName();
    }
  }, [companyLogoAndName, getCompanyLogoAndName, symbol]);

  useEffect(() => {
    if (isAddedToFavourites || isRemovedFromFavourites) {
      setTimeout(() => {
        setIsAddedToFavourites(false);
        setIsRemovedFromFavourites(false);
      }, PredefinedMilisecondsTypeEnum.eightSeconds);
    }
  }, [isAddedToFavourites, isRemovedFromFavourites]);

  return (
    <>
      {individual && (
      <ChartHeader
        {...headerProps}
        currentRange={currentRange}
        handleCreateOrderSellOrBuy={handleCreateOrderSellOrBuy}
        isSymbolInFavorites={isSymbolInFavourites(favouriteStocks, symbol)}
        toggleFavoritesSubscription={toggleFavoritesSubscription}
      />
      )}
      {checkIfSymbolIsHalted(symbolsStatusCache) && !isUserRestricted && (
        <div className={styles.haltedSymbolInfo}>
          <div>
            <img src={InfoIcon} alt="" />
          </div>
          <div>
            {lang.commonHaltedSymbolPopupContent2()}
          </div>
        </div>
      )}
      { (isRemovedFromFavourites || isAddedToFavourites) && (
        <FavouritesModal
          isMobile={isMobile}
          isSymbolDetails
          isAddedToFavourites={isAddedToFavourites}
          isRemovedFromFavourites={isRemovedFromFavourites}
          symbol={symbol}
          closeModal={() => {
            setIsAddedToFavourites(false);
            setIsRemovedFromFavourites(false);
          }}
        />
      )}
      { showFavouritesLimitMessage && (
        <FavouriteSymbolsLimitModal
          isSymbolDetails
          isMobile={isMobile}
          closeMessage={() => setShowFavouritesLimitMessage(false)}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState, ownProps: OwnProps) => ({
  favouriteStocks: getFavoriteStocks(state),
  companyLogoAndName: state.fundamental.companyLogoAndName,
  individual: state.crm.individualExtendedInfo,
  companyInfo: getCompany(ownProps.symbol, state),
});

const mapDispatchToProps = (dispatch: Dispatch<Action>, ownProps: OwnProps) => ({
  getCompanyLogoAndName: () => dispatch(getLogoAndName(ownProps.symbol)),
  addOrRemoveFromFavoriteList: (data: SymbolFavoriteListPayload) => dispatch(addOrRemoveSymbolToFavoriteList(data)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(SymbolDetailsHeader);
